import React from 'react';
import ReactDOM from 'react-dom/client';
import './index.css';
import App from './App';
import * as serviceWorkerRegistration from './serviceWorkerRegistration';
import reportWebVitals from './reportWebVitals';
import 'typeface-montserrat';
import { BrowserRouter as Router } from 'react-router-dom';
import { AuthProvider } from './contexts/AuthContext';
import { PlanProvider } from './contexts/PlanContext';

// Utility function to safely extract error message from various error types
const getErrorMessage = (error) => {
  if (!error) return 'Unknown error';
  if (typeof error === 'string') return error;
  if (error instanceof Error) return error.message || error.toString();
  if (error.message) return error.message;
  // Handle Event objects specifically
  if (error instanceof Event) {
    // Try to extract error from event.error if available
    if (error.error) {
      return getErrorMessage(error.error);
    }
    // Try to get message from event
    if (error.message) return error.message;
    return 'An unexpected event occurred';
  }
  // Try toString as last resort
  const errorString = String(error);
  // If it's [object Event] or similar, return a more helpful message
  if (errorString === '[object Event]' || errorString === '[object Object]') {
    return 'An unexpected error occurred';
  }
  return errorString;
};

// Global error handlers to catch unhandled errors
window.addEventListener('error', (event) => {
  // Prevent default error handling
  event.preventDefault();
  
  // Extract actual error from event
  const actualError = event.error || event;
  const errorMessage = getErrorMessage(actualError);
  
  // Log error but don't throw
  console.error('Unhandled error caught:', {
    message: errorMessage,
    originalMessage: event.message,
    filename: event.filename,
    lineno: event.lineno,
    colno: event.colno,
    error: actualError instanceof Error ? actualError : null
  });
  
  // Return true to prevent default error handling
  return true;
});

// Handle unhandled promise rejections
window.addEventListener('unhandledrejection', (event) => {
  // Prevent default error handling
  event.preventDefault();
  
  // Extract actual error from event.reason
  const actualError = event.reason || event;
  const errorMessage = getErrorMessage(actualError);
  
  // Log error but don't throw
  console.error('Unhandled promise rejection caught:', {
    reason: errorMessage,
    originalReason: event.reason,
    promise: event.promise
  });
  
  // Return true to prevent default error handling
  return true;
});

// Handle React errors that escape error boundaries
const originalConsoleError = console.error;
console.error = (...args) => {
  // Filter out known non-critical errors
  const errorString = args.join(' ');
  
  // Ignore certain React warnings that are not actual errors
  if (
    errorString.includes('Warning:') ||
    errorString.includes('ReactDOM.render') ||
    errorString.includes('findDOMNode')
  ) {
    // These are warnings, not errors - log but don't treat as critical
    originalConsoleError.apply(console, args);
    return;
  }
  
  // Log actual errors
  originalConsoleError.apply(console, args);
};

const root = ReactDOM.createRoot(document.getElementById('root'));
root.render(
  <React.StrictMode>
    <Router>
      <AuthProvider>
        <PlanProvider>
          <App />
        </PlanProvider>
      </AuthProvider>
    </Router>
  </React.StrictMode>
);

// If you want your app to work offline and load faster, you can change
// unregister() to register() below. Note this comes with some pitfalls.
// Learn more about service workers: https://cra.link/PWA
serviceWorkerRegistration.unregister();

// If you want to start measuring performance in your app, pass a function
// to log results (for example: reportWebVitals(console.log))
// or send to an analytics endpoint. Learn more: https://bit.ly/CRA-vitals
reportWebVitals();
